import React from 'react';
import { AssetUrls } from 'config/assets';

// Inline SVG card-back placeholder — never depends on S3/CDN, can't 403.
// Final fallback when all CDN sizes have failed.
export const CARD_BACK_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='223' height='311' viewBox='0 0 223 311'%3E%3Crect width='223' height='311' rx='12' fill='%231a1a2e'/%3E%3Crect x='8' y='8' width='207' height='295' rx='8' fill='none' stroke='%236F51E4' stroke-width='2' opacity='0.4'/%3E%3Ctext x='111.5' y='165' text-anchor='middle' fill='%236F51E4' font-size='48' opacity='0.5'%3E%E2%9C%A6%3C/text%3E%3C/svg%3E";

export interface CardImageInputs {
  image?: string | null;
  scryfallId?: string | null;
}

type CdnSize = 'normal' | 'small' | 'large' | 'art_crop';

// Resolve the best initial image URL for a card. Tries the backend-provided
// image URL first (run through ensureCDNUrl), then falls back to a CDN URL
// constructed from scryfallId, then to the inline data URI.
export const getCardImageUrl = (
  card: CardImageInputs,
  size: CdnSize = 'normal',
): string => {
  if (card.image) {
    let resolved = AssetUrls.cards.ensureCDNUrl(card.image);
    if (resolved && resolved.startsWith('http')) {
      if (size !== 'normal') {
        resolved = resolved
          .replace('/large/', `/${size}/`)
          .replace('/normal/', `/${size}/`)
          .replace('/small/', `/${size}/`)
          .replace('/art_crop/', `/${size}/`);
      }
      return resolved;
    }
  }
  if (card.scryfallId) {
    return AssetUrls.cards.getCardImage(card.scryfallId, size);
  }
  return CARD_BACK_DATA_URI;
};

// Build an onError handler that walks through CDN size variants for a card,
// falling back to the inline data URI when exhausted. Step state is tracked
// in a useRef<Map> keyed by a stable identifier — never via DOM dataset
// (which gets reset by React re-renders, causing infinite error loops).
export const makeCardImageErrorHandler = (
  card: CardImageInputs,
  fallbackStepRef: React.MutableRefObject<Map<string | number, number>>,
  key: string | number,
  initialSize: CdnSize = 'normal',
) => (e: React.SyntheticEvent<HTMLImageElement>) => {
  const step = fallbackStepRef.current.get(key) || 0;
  fallbackStepRef.current.set(key, step + 1);

  if (card.scryfallId) {
    const sizes: CdnSize[] = ['normal', 'small', 'large'];
    const initialUrl = getCardImageUrl(card, initialSize);
    const fallbacks = sizes
      .map(s => AssetUrls.cards.getCardImage(card.scryfallId as string, s))
      .filter(url => url !== initialUrl);
    if (step < fallbacks.length) {
      e.currentTarget.src = fallbacks[step];
      return;
    }
  }
  e.currentTarget.src = CARD_BACK_DATA_URI;
};
